import * as React from "react"
import { connect } from "react-redux"
import trans from "trans"
import { Link } from "react-router-dom"

import {
    css,
    Grid,
    GridCell,
    Button,
    modal,
    DataCell,
    DataTable,
    DataBody,
    DataHeader,
    DataRow,
    Theme,
    TextInput,
    Icon
} from "@bdfx/mergado-ui-kit"

import {Size} from "app/types/model-types";

import {
    deleteFormat as apiDeleteFormat,
    addFormat as apiAddFormat,
    editFormat as apiEditFormat,
    eshopId,
    projectId,
    getSizes
} from "app/helpers/api";

import {SettingsSpinner} from "./spinner";
import store from "store";
import {createToast} from "actions";
import toastFactory from "app/helpers/toast_factory";
import {SAVE_SIZES, setSizesToStore} from "template-edit/reducers";
import {URL} from "app/config/api-config";
import { AxiosResponse } from "axios"
import { find, sortBy } from "lodash"
import { ReduxList } from "app/types/app-types"

export type SizeItem = {
    id: string
    project_id: number
    name: string
    trans_string: string
    format: string
    width: number
    height: number
    templates: string[]
}

interface Props {
    setSizesToStore: typeof setSizesToStore
    sizes: ReduxList<SizeItem>
}

interface State {
    newFormat: SizeItem
    edited: SizeItem | null
    isSaving: boolean
}

const iconSize = 20

class SizesSettings extends React.Component<Props, State> {

    constructor(props: Props) {
        super(props)

        this.state = {
            newFormat: {
                id: 'new',
                trans_string: 'new',
                format: 'new',
                project_id: projectId,
                width: 1080,
                height: 1080,
                name: trans('new'),
                templates: [],
            },
            edited: null,
            isSaving: false,
        }
    }

    updateSizes = () => {
        getSizes()
            .then((response: AxiosResponse<SizeItem[]>) => {
                this.props.setSizesToStore(response.data)
            })
            .catch((e) => console.error('cannot get sizes', e))
    }

    componentDidMount() {
        this.updateSizes()
    }

    setEdited = (size: SizeItem) => {
        this.setState({edited: size})
    }

    findDefaultSize = () : SizeItem => {
        const {sizes} = this.props

        const sorted = sortBy(sizes?.data, [(size) => {
            return size.id
        }]);

        //find default size (1086x1086) - this size cannot be removed!
        return sorted ? find(sorted, (size: SizeItem) => {
            return (size.width === 1080 && size.height === 1080)
        }) : null
    }

    saveEdited = () => {
        const {edited} = this.state

        if (edited && edited.id) {
            this.setState({isSaving: true})
            apiEditFormat(edited)
                .then((response: any) => {
                    store.dispatch(createToast(toastFactory(SAVE_SIZES.SUCCESS, response.data)))
                    this.updateSizes()
                })
                .catch(error => {
                    store.dispatch(createToast(toastFactory(SAVE_SIZES.ERROR, error)))
                })
                .finally(() => {
                    this.setState({isSaving: false})
                })
        }

        this.setState({edited: null})
    }

    cancelEdited = () => {
        this.setState({edited: null})
    }

    showModal = (size : Size) => {
        modal({
            overlayClassName: 'sizes-settings-modal',
            title: <em>{trans('confirm_delete_format')}</em>, message: (close) =>
                <>
                    <div style={{padding: '20px 0'}}>
                        {trans('confirm_delete_format')} <em>{size.name}</em> ?
                    </div>

                    <Grid cols="repeat(auto-fit, minmax(100px, 1fr))" gap={"5px"}>
                        <GridCell style={{display: 'block'}}>
                            <Button
                                icon={<Icon type={'check-slim'} text={trans("confirm_delete_format_yes")}/>}
                                color='green'
                                size='small'
                                type='void'
                                secondary={true}
                                onClick={() => {
                                    apiDeleteFormat(size.id)
                                        .then((response: any) => {
                                            this.updateSizes()
                                        })
                                        .catch(error => {
                                            console.debug('error while deleting size', error.response.data.message, error.response.data.code)
                                        })
                                    close()
                                }} />
                        </GridCell>
                        <GridCell>
                            <Button
                                icon={<Icon type={'close'} text={trans("confirm_delete_format_no")}/>}
                                color='red'
                                type='void'
                                size='small'
                                secondary={true}
                                onClick={() => {
                                    close()
                                }}/>
                        </GridCell>
                    </Grid>
                </>
        })
    }

    handleEditSize = (size, isDefault) => {
        if (!isDefault) {
            this.setState({edited: size})
        }
    }

    render() {
        const {sizes} = this.props
        const {newFormat} = this.state

        if (sizes.data && sizes.isLoaded) {
            const defaultSize = this.findDefaultSize()
            const sortedSizes = sortBy(this.props.sizes.data, [(size) => {
                return size.id
            }])

            return (
                <StyledDataTable buttons={[]} className={sizes.isLoading ? 'muk-helper-loading' : ''} onRowSelected={() => {}}>
                    <DataHeader key="dataheader">
                        <DataCell type="header" style={{width: "11%"}}>
                            {trans('name')}
                        </DataCell>
                        <DataCell type="header" style={{width: "11%"}}>
                            {trans('width')}
                        </DataCell>
                        <DataCell type="header" style={{width: "11%"}}>
                            {trans('height')}
                        </DataCell>
                        <DataCell width={'33%'}>
                            {trans('action')}
                        </DataCell>
                        <DataCell type="header" style={{width: "33%"}}>
                            {trans('templates_having_this_size')}
                        </DataCell>
                    </DataHeader>

                    <DataBody sortable={false}>
                        {sortedSizes.filter(size => {
                            return ((size && size.id !== null && size.name !== null) || size.id === 'new')
                        }).map(size => {
                            const isDefault = (defaultSize && size.id === defaultSize.id);
                            const isEdited = (this.state.edited && size.id === this.state.edited.id);
                            const isUsed = (size.templates && Object.keys(size.templates).length > 0);

                            const {edited} = this.state

                            return <DataRow key={`row_${size.id}`} dataId={size.id}>
                                <DataCell onClick={() => this.handleEditSize(size, isDefault)}>
                                    {isEdited ?
                                    <TextInput
                                        disabled={isDefault}
                                        value={size.name}
                                        onChange={(e) => this.setState({edited: {...edited, name: e}})}
                                    /> :
                                        <span>{size.name}</span>
                                    }
                                </DataCell>
                                <DataCell onClick={() => this.handleEditSize(size, isDefault)}>
                                    {isEdited ?
                                        <TextInput
                                            disabled={isDefault}
                                            value={size.width}
                                            type={'number'}
                                            onChange={(e) => this.setState({edited: {...edited, width: e}})}
                                        /> :
                                        <span>{size.width}</span>
                                    }
                                </DataCell>
                                <DataCell onClick={() => this.handleEditSize(size, isDefault)}>
                                    {isEdited ?
                                        <TextInput
                                            disabled={isDefault}
                                            value={size.height}
                                            type={'number'}
                                            onChange={(e) => this.setState({edited: {...edited, height: e}})}
                                        /> :
                                        <span>{size.height}</span>
                                    }
                                </DataCell>

                                <DataCell className={'action-icons'}>
                                    {!isEdited && !isDefault &&
                                    <span className={'clickable'} title={trans("edit")} onClick={() => this.handleEditSize(size, isDefault)}><Icon type={'edit-light'} color={Theme.blue} size={iconSize} /></span>
                                    }
                                    {isEdited && (edited.width !== size.width || edited.height !== size.height || edited.name !== size.name) &&
                                    <span className={'clickable'} title={trans("save")} onClick={() => this.saveEdited()}>
                                        <Icon type={'save-light'} color={Theme.green} size={iconSize} />
                                    </span>
                                    }
                                    {isEdited &&
                                    <span className={'clickable'} title={trans("cancel")} onClick={() => this.cancelEdited()}>
                                        <Icon type={'close-light'} color={Theme.orange} size={iconSize} />
                                    </span>
                                    }
                                    {!isEdited && !isDefault && !isUsed &&
                                    <span className={'clickable'} title={trans("delete")} onClick={() => {
                                            this.showModal(size)
                                            close()
                                    }}>
                                        <Icon type={'trash-o'} color={Theme.red} size={iconSize} />
                                    </span>
                                    }
                                </DataCell>
                                <DataCell>
                                    {size.templates &&
                                    <StyledTemplatesGrid>
                                            {Object.keys(size.templates).map(id => {
                                                const thumbnailUrl = `${URL.laravelBase}images/thumbs/thumbnail${id}.${size.templates[id]?.is_anim_gif ? 'gif' : 'png'}`
                                                return <GridCell key={`template${id}`}>
                                                    <Link to={`/eshop/${eshopId}/project/${projectId}/templates/${id}/edit`}>
                                                    <img src={thumbnailUrl} title={size.templates[id]}/>
                                                </Link>
                                            </GridCell>
                                        })
                                        }
                                    </StyledTemplatesGrid>
                                    }
                                </DataCell>
                            </DataRow>
                        })}

                        <DataRow style={{backgroundColor: '#f2faf'}} dataId={'new'}>
                            <DataCell>
                                <TextInput
                                    value={this.state.newFormat.name}
                                    onChange={name => this.setState({ newFormat: {...this.state.newFormat, name: name} })}
                                />
                            </DataCell>
                            <DataCell>
                                <TextInput
                                    value={this.state.newFormat.width}
                                    type={'number'}
                                    onChange={width => this.setState({ newFormat: {...this.state.newFormat, width: width} })}
                                />
                            </DataCell>
                            <DataCell>
                                <TextInput
                                    value={this.state.newFormat.height}
                                    type={'number'}
                                    onChange={height => this.setState({ newFormat: {...this.state.newFormat, height: height} })}
                                />
                            </DataCell>

                            <DataCell key={`newsize_${newFormat.id}_buttons`}>
                                    <span onClick={() => {
                                        apiAddFormat(this.state.newFormat)
                                            .then((response: any) => {
                                                this.updateSizes()
                                            })
                                            .catch(error => {
                                                console.debug('error while adding new format', error.response.data.message, error.response.data.code)
                                            })
                                    }}>
                                        <Icon type={'check'} color={Theme.green} size={iconSize} title={trans("new")} />
                                    </span>
                            </DataCell>

                            <DataCell key={`newsize_${newFormat.id}_templates`} />
                        </DataRow>
                    </DataBody>
                </StyledDataTable>
            )
        } else {
            return (
                <SettingsSpinner />
            )
        }
    }
}

const StyledTemplatesGrid = css(Grid)({
    gridTemplateColumns: 'repeat(auto-fit, minmax(80px, 1fr))',
    ' img': {
        maxWidth: '80px',
        maxHeight: '80px',
    }
})

const StyledDataTable = css(DataTable)({
    ' .clickable': {
        cursor: 'pointer',
    },
})

function mapStateToProps(state) {
    return {
        sizes: state.sizes,
    }
}

export default connect(mapStateToProps, {
    setSizesToStore,
})(SizesSettings)

